import { apiRequest } from './client.js';

/**
 * Theme management (M01 §4 Theme Master, T004) — real /v1/themes contract.
 * Tenant is derived from the caller's session on the backend. `tokens` is a
 * free-form design-token bag; the UI surfaces the common colour tokens below.
 */

export const THEME_SCOPES = ['TENANT', 'GLOBAL'] as const;
export type ThemeScope = (typeof THEME_SCOPES)[number];

/** Colour tokens surfaced as typed inputs; serialised into `tokens` jsonb. */
export const THEME_TOKEN_FIELDS = [
  { key: 'primaryColor', label: 'Primary colour' },
  { key: 'secondaryColor', label: 'Secondary colour' },
  { key: 'accentColor', label: 'Accent colour' },
  { key: 'backgroundColor', label: 'Background colour' },
  { key: 'textColor', label: 'Text colour' },
  { key: 'successColor', label: 'Success colour' },
  { key: 'warningColor', label: 'Warning colour' },
  { key: 'dangerColor', label: 'Danger colour' },
  { key: 'borderColor', label: 'Border colour' },
] as const;

/** T004 theme record. */
export interface Theme {
  readonly id: string;
  readonly tenantId?: string | null;
  readonly themeCode: string;
  readonly themeName: string;
  readonly scope: ThemeScope;
  readonly schemaVersion: number;
  readonly tokens: Record<string, unknown>;
  /** A locked theme may be applied and nothing else — it is the way back. */
  readonly isLocked?: boolean;
  readonly recordStatusCode?: string;
  readonly effectiveFromDate?: string | null;
  readonly effectiveToDate?: string | null;
  readonly version: number;
}

export interface CreateThemeInput {
  readonly themeCode: string;
  readonly themeName: string;
  readonly scope?: ThemeScope;
  readonly schemaVersion?: number;
  readonly tokens: Record<string, unknown>;
}

export interface UpdateThemeInput {
  readonly themeName?: string;
  readonly schemaVersion?: number;
  readonly tokens?: Record<string, unknown>;
  readonly version: number;
}

type Tok = string | null | undefined;

function unwrap(
  r: { items: readonly Theme[] } | readonly Theme[] | null,
): readonly Theme[] {
  if (!r) return [];
  return Array.isArray(r) ? r : (r as { items: readonly Theme[] }).items ?? [];
}

/** GET /v1/themes — active themes for the current tenant. */
export async function listThemes(token?: Tok): Promise<readonly Theme[]> {
  const r = await apiRequest<{ items: readonly Theme[] } | readonly Theme[]>(
    '/themes',
    { token },
  );
  return unwrap(r);
}

/** GET /v1/themes/:id — a single theme. */
export function getTheme(id: string, token?: Tok): Promise<Theme> {
  return apiRequest<Theme>(`/themes/${encodeURIComponent(id)}`, { token });
}

/** POST /v1/themes — create a tenant-scoped theme. */
export function createTheme(
  body: CreateThemeInput,
  token?: Tok,
): Promise<Theme> {
  return apiRequest<Theme>('/themes', { method: 'POST', token, body });
}

/** PATCH /v1/themes/:id — update (optimistic concurrency via version). */
export function updateTheme(
  id: string,
  body: UpdateThemeInput,
  token?: Tok,
): Promise<Theme> {
  return apiRequest<Theme>(`/themes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    token,
    body,
  });
}

/** POST /v1/themes/:id/deactivate — soft-delete (record → INACTIVE). */
export function deactivateTheme(
  id: string,
  version: number,
  token?: Tok,
): Promise<{ id: string; status: string }> {
  return apiRequest<{ id: string; status: string }>(
    `/themes/${encodeURIComponent(id)}/deactivate`,
    { method: 'POST', token, body: { version } },
  );
}

/** DELETE /v1/themes/:id — logical delete (record → DELETED_LOGICAL). */
export function deleteTheme(
  id: string,
  version: number,
  token?: Tok,
): Promise<{ id: string; status: string }> {
  return apiRequest<{ id: string; status: string }>(
    `/themes/${encodeURIComponent(id)}`,
    { method: 'DELETE', token, body: { version } },
  );
}

/** POST /v1/themes/:id/reactivate — bring a deactivated theme back. */
export function reactivateTheme(
  id: string,
  version: number,
  token?: Tok,
): Promise<{ id: string; status: string }> {
  return apiRequest<{ id: string; status: string }>(
    `/themes/${encodeURIComponent(id)}/reactivate`,
    { method: 'POST', token, body: { version } },
  );
}
